import {
  DeleteOutlined,
  DollarOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { Breadcrumb, Button, Card, Descriptions, Tabs, Typography } from 'antd'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import client from '@/api/client'
import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { FormattedDate } from '@/components/common/FormattedDate'
import { MONO_STYLE } from '@/components/common/MonoId'
import { RecordFallback } from '@/components/common/RecordFallback'
import { ResourceBillingTab } from '@/components/common/ResourceBillingTab'
import { StatusTag } from '@/components/common/StatusTag'
import { TerminateConfirmModal } from '@/components/common/TerminateConfirmModal'
import { TroubleshootCard } from '@/components/common/TroubleshootCard'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { VmNameLink } from '@/components/common/VmNameLink'
import { STORAGE_TERMINAL_STATUSES } from '@/constants/statuses'
import { useAdminRecord } from '@/hooks/useAdminRecord'
import { useDetailTermination } from '@/hooks/useDetailTermination'
import { VmsCacheProvider } from '@/hooks/useVmsByIds'

type UserStorageDto = components['schemas']['UserStorageDto']

export default function StorageShowPage() {
  const { id: storageId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'info'
  const { record: storage, query: storageQuery } =
    useAdminRecord<UserStorageDto>('storages', storageId)

  const { terminateOpen, openTerminate, closeTerminate, handleTerminate } =
    useDetailTermination({
      resourceLabel: 'Storage',
      terminateFn: () =>
        client.POST('/v1/admin/storages/terminate', {
          body: { storageIds: [storageId ?? ''] },
        }),
      onSuccess: () => void storageQuery.refetch(),
    })

  if (!storageId) return null

  if (!storage) {
    return (
      <RecordFallback
        label="Storage"
        isLoading={storageQuery.isLoading}
        error={storageQuery.error}
      />
    )
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/storages">Storage</Link> },
          { title: storage.name },
        ]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0 }}>
          Storage: <Typography.Text copyable>{storage.name}</Typography.Text>
        </h2>
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={openTerminate}
          disabled={STORAGE_TERMINAL_STATUSES.has(storage.status)}
        >
          Terminate
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
        items={[
          {
            key: 'info',
            label: 'Info',
            icon: <InfoCircleOutlined />,
            children: <StorageInfoTab storage={storage} />,
          },
          {
            key: 'billing',
            label: 'Billing',
            icon: <DollarOutlined />,
            children: (
              <ResourceBillingTab
                userId={storage.userId}
                resourceId={storageId}
              />
            ),
          },
        ]}
      />

      <TerminateConfirmModal
        open={terminateOpen}
        title="Terminate Storage"
        resourceName={storage.name}
        onConfirm={handleTerminate}
        onCancel={closeTerminate}
      />
    </div>
  )
}

function StorageInfoTab({ storage }: { storage: UserStorageDto }) {
  return (
    <VmsCacheProvider ids={storage.attachedTo}>
      <div style={{ display: 'grid', gap: 24 }}>
        <Card>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="ID">
              <Typography.Text copyable style={MONO_STYLE}>
                {storage.id}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Name">
              <Typography.Text copyable>{storage.name}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <StatusTag status={storage.status} />
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              {storage.storageType}
            </Descriptions.Item>
            <Descriptions.Item label="Volume">
              {storage.volumeGb} GB
            </Descriptions.Item>
            <Descriptions.Item label="Role">{storage.role}</Descriptions.Item>
            <Descriptions.Item label="User">
              <UserEmailLink userId={storage.userId} />
            </Descriptions.Item>
            <Descriptions.Item label="Cluster">
              <ClusterNameLink clusterId={storage.clusterId} />
            </Descriptions.Item>
            <Descriptions.Item label="Attached To">
              {storage.attachedTo.length > 0
                ? storage.attachedTo.map((vmId) => (
                    <div key={vmId}>
                      <VmNameLink vmId={vmId} />
                    </div>
                  ))
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Init URL">
              {storage.initUrl ? (
                <Typography.Text copyable style={{ fontSize: 12 }}>
                  {storage.initUrl}
                </Typography.Text>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              <FormattedDate date={storage.createdAt} fmt="PPp" />
            </Descriptions.Item>
            <Descriptions.Item label="Ready Since">
              {storage.readySince ? (
                <FormattedDate date={storage.readySince} fmt="PPp" />
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Last Paid">
              <FormattedDate date={storage.lastPaidAt} fmt="PPp" />
            </Descriptions.Item>
            <Descriptions.Item label="Paid Until">
              <FormattedDate date={storage.paidUntil} fmt="PPp" />
            </Descriptions.Item>
            <Descriptions.Item label="Removed">
              <FormattedDate date={storage.removedAt} fmt="PPp" />
            </Descriptions.Item>
          </Descriptions>
        </Card>
        <TroubleshootCard
          kind="storage"
          userId={storage.userId}
          resourceId={storage.id}
        />
      </div>
    </VmsCacheProvider>
  )
}
